import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_SUPPORT_TICKET, UPDATE_SUPPORT_TICKET, ADD_SUPPORT_TICKET_COMMENT } from '../../graphql/support';
import useMainStore from '../../store/mainState';
import GButton from '../../components/ui/base/GButton';

interface TicketDetailProps {
    ticketId: number;
    onBack: () => void;
}

const STATUS_COLORS: Record<string, string> = {
    OPEN: 'bg-[#4DBDFF]/15 text-[#4DBDFF] border-[#4DBDFF]/20',
    IN_PROGRESS: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
    RESOLVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    CLOSED: 'bg-white/[0.06] text-white/40 border-white/[0.08]',
    PENDING: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
};

const PRIORITY_COLORS: Record<string, string> = {
    LOW: 'text-white/30',
    MEDIUM: 'text-yellow-400',
    HIGH: 'text-orange-400',
    URGENT: 'text-red-400',
};

const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const TicketDetail: React.FC<TicketDetailProps> = ({ ticketId, onBack }) => {
    const { t } = useTranslation();
    const { user } = useMainStore();
    const [comment, setComment] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState('');

    const { data, loading, error, refetch } = useQuery(GET_SUPPORT_TICKET, {
        variables: { id: ticketId },
        fetchPolicy: 'cache-and-network',
    });

    const [updateTicket, { loading: updateLoading }] = useMutation(UPDATE_SUPPORT_TICKET, { errorPolicy: 'all' });
    const [addComment, { loading: commentLoading }] = useMutation(ADD_SUPPORT_TICKET_COMMENT, { errorPolicy: 'all' });

    const ticket = data?.supportTicket;
    const isOwner = user?.id && ticket?.user?.id && Number(user.id) === Number(ticket.user.id);
    const isOpen = ticket?.status === 'OPEN' || ticket?.status === 'IN_PROGRESS' || ticket?.status === 'PENDING';

    const handleAddComment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!comment.trim()) return;
        try {
            const result = await addComment({
                variables: {
                    input: {
                        ticketId,
                        content: comment.trim(),
                    },
                },
            });
            if (!result.errors) {
                setComment('');
                refetch();
            }
        } catch (err) {
            console.error('Failed to add comment:', err);
        }
    };

    const handleUpdateTitle = async () => {
        if (!newTitle.trim() || newTitle.trim() === ticket?.title) {
            setEditingTitle(false);
            return;
        }
        try {
            await updateTicket({
                variables: {
                    input: {
                        id: ticketId,
                        title: newTitle.trim(),
                    },
                },
            });
            setEditingTitle(false);
            refetch();
        } catch (err) {
            console.error('Failed to update ticket:', err);
        }
    };

    if (loading && !ticket) {
        return (
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px] animate-pulse">
                <div className="h-[20px] bg-white/[0.06] rounded w-1/3 mb-[16px]"></div>
                <div className="h-[14px] bg-white/[0.04] rounded w-2/3 mb-[8px]"></div>
                <div className="h-[14px] bg-white/[0.04] rounded w-1/2"></div>
            </div>
        );
    }

    if (error || !ticket) {
        return (
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[40px] flex flex-col items-center gap-[16px]">
                <p className="text-white/40 text-[14px] font-medium">{t('support.ticketNotFound')}</p>
                <GButton variant="secondary" className="py-[8px] px-[20px] rounded-[12px]" onClick={onBack}>
                    {t('support.backToTickets')}
                </GButton>
            </div>
        );
    }

    return (
        <div className="space-y-[16px]">
            {/* Ticket Header Card */}
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px]">
                {/* Title */}
                <div className="flex items-start gap-[12px] mb-[16px]">
                    <span className="text-white/15 text-[13px] font-semibold mt-[4px]">#{ticket.id}</span>
                    <div className="flex-1 min-w-0">
                        {editingTitle ? (
                            <div className="flex items-center gap-[8px]">
                                <input
                                    type="text"
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    className="flex-1 px-[12px] py-[8px] rounded-[10px] bg-white/[0.06] border border-white/[0.1] text-white text-[15px] font-bold outline-none focus:border-[#4DBDFF]/50 transition-all duration-300"
                                    autoFocus
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                                />
                                <GButton variant="primary" className="py-[6px] px-[14px] rounded-[8px]" onClick={handleUpdateTitle} disabled={updateLoading}>
                                    <span className="text-[12px]">{t('common.save')}</span>
                                </GButton>
                                <GButton variant="secondary" className="py-[6px] px-[14px] rounded-[8px]" onClick={() => setEditingTitle(false)}>
                                    <span className="text-[12px]">{t('common.cancel')}</span>
                                </GButton>
                            </div>
                        ) : (
                            <div className="flex items-center gap-[8px]">
                                <h2 className="text-white text-[20px] font-bold">{ticket.title}</h2>
                                {isOwner && isOpen && (
                                    <button
                                        onClick={() => { setNewTitle(ticket.title); setEditingTitle(true); }}
                                        className="text-white/20 hover:text-[#4DBDFF] transition-colors duration-200"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Meta row */}
                <div className="flex flex-wrap gap-[8px] mb-[16px]">
                    <span className={`text-[11px] font-semibold px-[8px] py-[3px] rounded-full border ${STATUS_COLORS[ticket.status] ?? STATUS_COLORS.OPEN}`}>
                        {ticket.status?.replace('_', ' ')}
                    </span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${PRIORITY_COLORS[ticket.priority] ?? 'text-white/30'}`}>
                        {ticket.priority}
                    </span>
                    <span className="text-white/20 text-[11px] font-medium">
                        {ticket.category}
                    </span>
                </div>

                {/* Description */}
                <div className="bg-white/[0.02] rounded-[12px] border border-white/[0.04] p-[16px] mb-[16px]">
                    <p className="text-white/50 text-[14px] font-medium leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
                </div>

                {/* Info row */}
                <div className="flex flex-wrap gap-x-[24px] gap-y-[6px] text-[12px] text-white/25 font-medium">
                    <span>{t('support.createdBy')}: <span className="text-white/40">{ticket.user?.username}</span></span>
                    <span>{t('support.created')}: <span className="text-white/40">{formatDate(ticket.createdAt)}</span></span>
                    {ticket.assignedUser && (
                        <span>{t('support.assignedTo')}: <span className="text-white/40">{ticket.assignedUser.username}</span></span>
                    )}
                    {ticket.updatedAt !== ticket.createdAt && (
                        <span>{t('support.updated')}: <span className="text-white/40">{formatDate(ticket.updatedAt)}</span></span>
                    )}
                </div>
            </div>

            {/* Comments */}
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px]">
                <h3 className="text-white text-[16px] font-bold mb-[16px]">
                    {t('support.comments')} ({ticket.comments?.length ?? 0})
                </h3>

                {ticket.comments && ticket.comments.length > 0 ? (
                    <div className="space-y-[10px] mb-[20px]">
                        {ticket.comments.map((c: any) => {
                            const isCurrentUser = user?.id && Number(user.id) === Number(c.user?.id);
                            return (
                                <div
                                    key={c.id}
                                    className={`rounded-[12px] p-[14px] border ${
                                        isCurrentUser
                                            ? 'bg-[#4DBDFF]/[0.04] border-[#4DBDFF]/10'
                                            : 'bg-white/[0.02] border-white/[0.04]'
                                    }`}
                                >
                                    <div className="flex items-center gap-[8px] mb-[6px]">
                                        <span className={`text-[13px] font-bold ${isCurrentUser ? 'text-[#4DBDFF]' : 'text-white/60'}`}>
                                            {c.user?.username}
                                        </span>
                                        <span className="text-white/15 text-[11px] font-medium">{formatDate(c.createdAt)}</span>
                                    </div>
                                    <p className="text-white/50 text-[14px] font-medium leading-relaxed whitespace-pre-wrap">{c.content}</p>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-white/20 text-[13px] font-medium mb-[20px]">{t('support.noComments')}</p>
                )}

                {/* Add comment form */}
                {isOpen ? (
                    <form onSubmit={handleAddComment} className="space-y-[10px]">
                        <textarea
                            placeholder={t('support.addCommentPlaceholder')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            className="w-full px-[16px] py-[12px] rounded-[12px] bg-white/[0.06] border border-white/[0.1] text-white text-[14px] font-medium outline-none placeholder-white/20 focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)] transition-all duration-300 resize-none"
                        />
                        <GButton
                            variant="primary"
                            className="py-[10px] px-[20px] rounded-[12px]"
                            disabled={commentLoading || !comment.trim()}
                            type="submit"
                        >
                            {commentLoading ? t('support.sending') : t('support.addComment')}
                        </GButton>
                    </form>
                ) : (
                    <div className="bg-white/[0.02] rounded-[12px] border border-white/[0.04] p-[12px] text-center">
                        <p className="text-white/25 text-[13px] font-medium">{t('support.ticketClosed')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TicketDetail;
